import React, { useState, useEffect } from 'react';
import styles from './subscriptions.module.scss';
import { Fade } from 'react-awesome-reveal';
import { Typography } from '../Typography';
import { Button } from '../Button';
import { subscriptionService, SubscriptionPlan } from '../../utils/subscriptionService';
import { useRouter } from 'next/router';
import toast from 'react-hot-toast';
import SubscriptionModal from './SubscriptionModal';
import { getProductIdBySlug } from '../../data/products';

const CircleBoltIcon: React.FC<{ size?: number }> = ({ size = 48 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 48 48"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M0 24C0 10.7452 10.7452 0 24 0C37.2548 0 48 10.7452 48 24C48 37.2548 37.2548 48 24 48C10.7452 48 0 37.2548 0 24Z"
      fill="#7F56D9"
    />
    <path
      d="M24.9989 14L16.0923 24.6879C15.7435 25.1064 15.5691 25.3157 15.5665 25.4925C15.5641 25.6461 15.6326 25.7923 15.7521 25.8889C15.8896 26 16.1621 26 16.7069 26H23.9989L22.9989 34L31.9054 23.3121C32.2542 22.8936 32.4286 22.6843 32.4313 22.5075C32.4336 22.3539 32.3652 22.2077 32.2456 22.1111C32.1081 22 31.8357 22 31.2909 22H23.9989L24.9989 14Z"
      stroke="#E9D7FE"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CheckIcon: React.FC<{ size?: number }> = ({ size = 24 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M0 12C0 5.37258 5.37258 0 12 0C18.6274 0 24 5.37258 24 12C24 18.6274 18.6274 24 12 24C5.37258 24 0 18.6274 0 12Z"
      fill="#9E77ED"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M17.098 7.39004L9.93797 14.3L8.03797 12.27C7.68797 11.94 7.13797 11.92 6.73797 12.2C6.34797 12.49 6.23797 13 6.47797 13.41L8.72797 17.07C8.94797 17.41 9.32797 17.62 9.75797 17.62C10.168 17.62 10.558 17.41 10.778 17.07C11.138 16.6 18.008 8.41004 18.008 8.41004C18.908 7.49004 17.818 6.68004 17.098 7.38004V7.39004Z"
      fill="#E9D7FE"
    />
  </svg>
);

type SubscriptionsProps = {
  selectedPlanId?: string | null;
  onSelect?: (planId: string) => void;
  onWidthChange?: (width: number) => void;
  isDisabled?: boolean;
  currency?: 'usd' | 'eur';
  onCurrencyChange?: (currency: 'usd' | 'eur') => void;
};

const Subscriptions: React.FC<SubscriptionsProps> = ({
  selectedPlanId = null,
  onSelect,
  onWidthChange,
  isDisabled = false,
  currency: externalCurrency,
  onCurrencyChange,
}) => {
  const router = useRouter();
  const slug = (router.query.url as string) || '';
  const productId = slug ? getProductIdBySlug(slug) || '' : '';
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [internalCurrency, setInternalCurrency] = useState<'usd' | 'eur'>('eur');
  const currency = externalCurrency !== undefined ? externalCurrency : internalCurrency;
  const [modalOpen, setModalOpen] = useState(false);
  const [modalSelectedPlanId, setModalSelectedPlanId] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(false);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const data = await subscriptionService.getPlans({
        isActive: true,
        hidden: false,
      });
      // Sort plans by price (cheapest to most expensive based on selected currency)
      const sortedPlans = [...data].sort((a, b) => {
        const priceA = currency === 'usd' ? a.price.usd : a.price.eur;
        const priceB = currency === 'usd' ? b.price.usd : b.price.eur;
        return priceA - priceB;
      });
      setPlans(sortedPlans);
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
      setPlans([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
    // Detect user currency preference
    if (externalCurrency === undefined) {
      const userLang = typeof window !== 'undefined' ? navigator.language : 'en-US';
      if (
        userLang.toLowerCase().includes('eu') ||
        userLang.toLowerCase().includes('de') ||
        userLang.toLowerCase().includes('fr')
      ) {
        const detectedCurrency = 'eur';
        setInternalCurrency(detectedCurrency);
        onCurrencyChange?.(detectedCurrency);
      } else {
        const detectedCurrency = 'eur';
        setInternalCurrency(detectedCurrency);
        onCurrencyChange?.(detectedCurrency);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalCurrency, onCurrencyChange]);

  useEffect(() => {
    const onResize = () => {
      setIsMobile(typeof window !== 'undefined' && window.innerWidth <= 480);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  // Re-sort plans when currency changes
  useEffect(() => {
    if (plans.length > 0) {
      setPlans((prevPlans) => {
        const sortedPlans = [...prevPlans].sort((a, b) => {
          const priceA = currency === 'usd' ? a.price.usd : a.price.eur;
          const priceB = currency === 'usd' ? b.price.usd : b.price.eur;
          return priceA - priceB;
        });
        return sortedPlans;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency]);

  const handleCurrencyChange = (newCurrency: 'usd' | 'eur') => {
    if (externalCurrency === undefined) {
      setInternalCurrency(newCurrency);
    }
    onCurrencyChange?.(newCurrency);
  };

  const handleSelectCard = (planId: string) => {
    setModalSelectedPlanId(planId);
    setModalOpen(true);
    onSelect?.(planId);
  };

  const formatPrice = (price: { usd: number; eur: number }, selectedCurrency: 'usd' | 'eur') => {
    const amount = selectedCurrency === 'usd' ? price.usd : price.eur;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: selectedCurrency.toUpperCase(),
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Default plans if API returns empty (for demo/fallback)
  const defaultPlans: SubscriptionPlan[] = [
    {
      _id: '1',
      name: 'Basic plan',
      description: 'Basic features for up to 10 users.',
      hostLimit: 5,
      hardwareLimit: 2,
      price: {
        usd: 10,
        eur: 9,
      },
      billingCycle: 'monthly',
      features: [
        'Access to basic features',
        'Basic reporting and analytics',
        'Up to 10 individual users',
        '20 GB individual data',
        'Basic chat and email support',
      ],
      duration: 1,
      isActive: true,
      hidden: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    {
      _id: '2',
      name: 'Business plan',
      description: 'Advanced features and reporting.',
      hostLimit: 15,
      hardwareLimit: 5,
      price: {
        usd: 20,
        eur: 18,
      },
      billingCycle: 'monthly',
      features: [
        'Access to basic features',
        'Basic reporting and analytics',
        'Up to 10 individual users',
        '20 GB individual data',
        'Basic chat and email support',
      ],
      duration: 1,
      isActive: true,
      hidden: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    {
      _id: '3',
      name: 'Enterprise plan',
      description: 'Unlimited features.',
      hostLimit: 50,
      hardwareLimit: 10,
      price: {
        usd: 40,
        eur: 36,
      },
      billingCycle: 'monthly',
      features: [
        'Access to basic features',
        'Basic reporting and analytics',
        'Up to 10 individual users',
        '20 GB individual data',
        'Basic chat and email support',
      ],
      duration: 1,
      isActive: true,
      hidden: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
  ];

  // Sort default plans by price as well
  const sortedDefaultPlans = [...defaultPlans].sort((a, b) => {
    const priceA = currency === 'usd' ? a.price.usd : a.price.eur;
    const priceB = currency === 'usd' ? b.price.usd : b.price.eur;
    return priceA - priceB;
  });
  const displayPlans = plans.length > 0 ? plans : sortedDefaultPlans;

  // Compute modal width to fit exact number of cards (1-3)
  useEffect(() => {
    const count = loading && plans.length === 0 ? 3 : Math.min(displayPlans.length, 3);
    const cardWidth = 340; // px
    const gap = 32; // from styles.plansGrid
    const modalBodyPadding = 48; // Modal content padding (24 left + 24 right)
    const width = count * cardWidth + Math.max(0, count - 1) * gap + modalBodyPadding;
    onWidthChange?.(width);
  }, [displayPlans.length, loading, plans.length, onWidthChange]);

  return (
    <>
      <div className={styles.subscriptionsContainer}>
        <div
          className={`${styles.subscriptionsContent} ${
            isDisabled ? styles.contentDisabled : ''
          }`}
          aria-disabled={isDisabled}
        >
          {loading && plans.length === 0 ? (
            <div className={styles.loading}>
              <div className={styles.plansGrid}>
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className={styles.planCard} style={{ opacity: 0.4 }}>
                    {/* Icon skeleton */}
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
                      <div className={styles.skeletonIcon} />
                    </div>
                    {/* Title skeleton */}
                    <div className={styles.skeletonTitle} />
                    {/* Price skeleton */}
                    <div className={styles.skeletonPrice} />
                    {/* Features skeleton */}
                    {[0, 1, 2, 3, 4].map((j) => (
                      <div key={j} className={styles.skeletonFeature}>
                        <div className={styles.skeletonCheckIcon} />
                        <div className={styles.skeletonFeatureText} style={{ width: `${70 + j * 10}%` }} />
                      </div>
                    ))}
                    {/* Button skeleton */}
                    <div className={styles.skeletonButtonContainer}>
                      <div className={styles.skeletonButton} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div
              className={`${styles.plansGrid} ${
                displayPlans.length === 1
                  ? styles.singleGrid
                  : displayPlans.length === 2
                    ? styles.twoGrid
                    : ''
              }`}
            >
              {displayPlans.map((plan, index) => (
                <Fade
                  key={plan._id || `plan-${index}`}
                  triggerOnce
                  duration={1000}
                  delay={index * 200}
                  style={{ width: '100%' }}
                >
                  <div className={styles.planCard}>
                    <div className={styles.planHeader} style={{ alignItems: 'center', textAlign: 'center' }}>
                      <div style={{ marginBottom: 8, display: 'inline-flex' }}>
                        <CircleBoltIcon size={48} />
                      </div>
                      <Typography variant="text-xl" color="grayDark-50" weight="semibold">
                        {plan.name}
                      </Typography>
                      <Typography variant="display-lg" color="grayDark-50" weight="semibold" style={{ marginTop: 8 }}>
                        {`${formatPrice(plan.price, currency)}/mth`}
                      </Typography>
                    </div>
                    <ul
                      className={styles.planFeatures}
                      style={{ listStyle: 'none', padding: 0, margin: 0, borderTop: 'none' }}
                    >
                      {plan.features.map((feature, idx) => (
                        <li
                          key={idx}
                          style={{ display: 'flex', alignItems: 'center', gap: 12 }}
                        >
                          <span style={{ display: 'inline-flex', flexShrink: 0 }}>
                            <CheckIcon size={20} />
                          </span>
                          <Typography variant="text-md" color="grayDark-300" style={{ flex: 1 }}>
                            {feature}
                          </Typography>
                        </li>
                      ))}
                    </ul>
                    <div
                      className={styles.planCta}
                      style={{
                        borderTop: '1px solid var(--color-gray-dark-700)',
                        marginTop: 24,
                        marginLeft: -32,
                        marginRight: -32,
                        marginBottom: -32,
                        padding: '24px 32px',
                        background: 'rgba(255, 255, 255, 0.03)',
                        borderBottomLeftRadius: 16,
                        borderBottomRightRadius: 16,
                      }}
                    >
                      <div style={{ width: '100%' }}>
                        <Button
                          variant="primary"
                          onClick={() => {
                            if (plan._id) {
                              handleSelectCard(plan._id);
                            }
                          }}
                          fullWidth
                        >
                          Get started
                        </Button>
                      </div>
                    </div>
                  </div>
                </Fade>
              ))}
            </div>
          )}
        </div>
      </div>
      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setModalSelectedPlanId(null);
        }}
        topOffset={isMobile ? 56 : 72}
        productId={productId}
        selectedPlanId={modalSelectedPlanId}
      />
    </>
  );
};

export default Subscriptions;
